import React, { useEffect, useState, useCallback } from "react";
import { Wallet as WalletIcon, Gift, Megaphone, Plus, Search, Copy, Trash2, Power, Send, BarChart3, Trophy } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";

/* Admin Wallets / Gift Cards / Promotions tab — three sections in one card. */
export default function WalletAdminTab() {
  return (
    <div data-testid="wallet-admin-tab" className="space-y-8">
      <header>
        <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2"><WalletIcon className="w-3.5 h-3.5" /> Wallet operations</div>
        <h2 className="font-display font-black text-3xl text-white tracking-tighter mt-1">Wallets, Gift cards & Promotions</h2>
        <p className="text-white/55 mt-1 text-sm">Credit user wallets, mint gift cards, and run reward campaigns.</p>
      </header>
      <UsersSection />
      <GiftCardsSection />
      <PromotionsSection />
      <BlastsLeaderboard />
      <LiveAdRevenueSection />
    </div>
  );
}

/* ─────────────────────────── Users section ─────────────────────────── */
function UsersSection() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [creditFor, setCreditFor] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/admin/wallet/users?q=${encodeURIComponent(q)}`);
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <section className="rounded-2xl tv-glass p-5" data-testid="wa-users-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="font-display font-black text-xl text-white">User wallets</h3>
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input
            data-testid="wa-users-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search email or name…"
            className="bg-white/5 border border-white/15 rounded-lg pl-9 pr-3 py-2 text-white placeholder-white/30 outline-none focus:border-tv-accent text-sm w-64"
          />
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
            <tr><th className="py-2 px-2">User</th><th className="py-2 px-2">Email</th><th className="py-2 px-2 text-right">Cash</th><th className="py-2 px-2 text-right">Points</th><th className="py-2 px-2">Stripe</th><th className="py-2 px-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((w) => (
              <tr key={w.user_id} data-testid={`wa-user-row-${w.user_id}`} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 px-2">{w.user?.name || "—"}</td>
                <td className="py-2 px-2 text-white/70">{w.user?.email || w.user_id}</td>
                <td className="py-2 px-2 text-right font-mono text-emerald-300">${(w.cash_cents / 100).toFixed(2)}</td>
                <td className="py-2 px-2 text-right font-mono text-pink-300">{w.points.toLocaleString()}</td>
                <td className="py-2 px-2 text-xs">{w.payouts_enabled ? <span className="text-emerald-300">Ready</span> : w.stripe_account_id ? <span className="text-amber-300">Pending</span> : <span className="text-white/40">—</span>}</td>
                <td className="py-2 px-2 text-right">
                  <button data-testid={`wa-credit-${w.user_id}`} onClick={() => setCreditFor(w)} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1">
                    <Plus className="w-3 h-3" /> Credit
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-white/40">No wallets yet</td></tr>}
          </tbody>
        </table>
      </div>
      {creditFor && (
        <CreditModal user={creditFor} onClose={() => setCreditFor(null)} onDone={() => { setCreditFor(null); load(); }} />
      )}
    </section>
  );
}

function CreditModal({ user, onClose, onDone }) {
  const [cash, setCash] = useState("0.00");
  const [points, setPoints] = useState("0");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const cents = Math.round(Number(cash) * 100);
    const pts = Math.round(Number(points));
    if (!cents && !pts) { toast.error("Enter cash or points"); return; }
    setBusy(true);
    try {
      await api.post("/admin/wallet/credit", { user_id: user.user_id, cash_cents: cents, points: pts, note: note.trim() });
      toast.success("Credited");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="tv-glass rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="wa-credit-modal">
        <h3 className="font-display font-black text-xl text-white mb-1">Credit wallet</h3>
        <div className="text-white/60 text-sm mb-4">{user.user?.email || user.user_id}</div>
        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Cash (USD)</label>
        <input data-testid="wa-credit-cash" value={cash} onChange={(e) => setCash(e.target.value.replace(/[^0-9.]/g, ""))} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent" />
        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mt-3 mb-1">Points</label>
        <input data-testid="wa-credit-points" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent" />
        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mt-3 mb-1">Note (optional)</label>
        <input data-testid="wa-credit-note" value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent" />
        <div className="flex gap-2 mt-5">
          <button onClick={onClose} className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-2 rounded-lg">Cancel</button>
          <button data-testid="wa-credit-submit" onClick={submit} disabled={busy} className="flex-1 bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2 rounded-lg disabled:opacity-50">{busy ? "Crediting…" : "Credit"}</button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────── Gift Cards section ─────────────────────────── */
function GiftCardsSection() {
  const [rows, setRows] = useState([]);
  const [cash, setCash] = useState("10.00");
  const [points, setPoints] = useState("0");
  const [qty, setQty] = useState("1");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/wallet/giftcards?status=all&limit=200");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    const cents = Math.round(Number(cash) * 100);
    const pts = Math.round(Number(points));
    const q = Math.max(1, Math.min(500, Math.round(Number(qty)) || 1));
    if (!cents && !pts) { toast.error("Set cash or points"); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/wallet/giftcards", { cash_cents: cents, points: pts, quantity: q });
      toast.success(`Created ${r.data.count} gift card${r.data.count > 1 ? "s" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const voidCard = async (code) => {
    try { await api.post(`/admin/wallet/giftcards/${code}/void`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Copied"); } catch {}
  };

  return (
    <section className="rounded-2xl tv-glass p-5" data-testid="wa-giftcards-section">
      <div className="flex items-center gap-2 mb-3">
        <Gift className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl text-white">Gift cards</h3>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <div>
          <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Cash $</label>
          <input data-testid="wa-gc-cash" value={cash} onChange={(e) => setCash(e.target.value.replace(/[^0-9.]/g, ""))} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none text-sm" />
        </div>
        <div>
          <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Points</label>
          <input data-testid="wa-gc-points" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none text-sm" />
        </div>
        <div>
          <label className="block text-white/50 text-[10px] uppercase tracking-wider mb-1">Quantity</label>
          <input data-testid="wa-gc-qty" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ""))} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none text-sm" />
        </div>
        <button data-testid="wa-gc-create" onClick={create} disabled={busy} className="self-end bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2 rounded-lg inline-flex items-center justify-center gap-1 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Mint
        </button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
            <tr><th className="py-2 px-2">Code</th><th className="py-2 px-2 text-right">Cash</th><th className="py-2 px-2 text-right">Points</th><th className="py-2 px-2">Status</th><th className="py-2 px-2"></th></tr>
          </thead>
          <tbody>
            {rows.slice(0, 100).map((c) => (
              <tr key={c.code} className="border-b border-white/5 hover:bg-white/5">
                <td className="py-2 px-2 font-mono text-xs">{c.code}</td>
                <td className="py-2 px-2 text-right text-emerald-300">${(c.cash_cents / 100).toFixed(2)}</td>
                <td className="py-2 px-2 text-right text-pink-300">{c.points}</td>
                <td className="py-2 px-2">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-black uppercase ${c.status === "active" ? "bg-emerald-500/20 text-emerald-300" : c.status === "redeemed" ? "bg-white/10 text-white/50" : "bg-rose-500/20 text-rose-300"}`}>{c.status}</span>
                </td>
                <td className="py-2 px-2 text-right space-x-1">
                  <button onClick={() => copyCode(c.code)} className="text-white/60 hover:text-white p-1" title="Copy"><Copy className="w-3.5 h-3.5" /></button>
                  {c.status === "active" && <button onClick={() => voidCard(c.code)} className="text-rose-400 hover:text-rose-300 p-1" title="Void"><Trash2 className="w-3.5 h-3.5" /></button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="py-6 text-center text-white/40">No gift cards yet</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ─────────────────────────── Promotions section ─────────────────────────── */
function PromotionsSection() {
  const [rows, setRows] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [cash, setCash] = useState("0.00");
  const [points, setPoints] = useState("0");
  const [criteria, setCriteria] = useState("manual");
  const [slug, setSlug] = useState("");
  const [headline, setHeadline] = useState("");
  const [subhead, setSubhead] = useState("");
  const [busy, setBusy] = useState(false);
  const [awardFor, setAwardFor] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/admin/promotions");
      setRows(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    const cents = Math.round(Number(cash) * 100);
    const pts = Math.round(Number(points));
    if (!cents && !pts) { toast.error("Set cash and/or points reward"); return; }
    setBusy(true);
    try {
      await api.post("/admin/promotions", {
        name: name.trim(), description: desc.trim(),
        reward_cents: cents, reward_points: pts, criteria_type: criteria,
        slug: slug.trim() || undefined,
        landing_headline: headline.trim() || undefined,
        landing_subhead: subhead.trim() || undefined,
      });
      toast.success("Promotion created");
      setName(""); setDesc(""); setCash("0.00"); setPoints("0");
      setSlug(""); setHeadline(""); setSubhead("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const toggle = async (pid) => {
    try { await api.post(`/admin/promotions/${pid}/toggle`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="rounded-2xl tv-glass p-5" data-testid="wa-promos-section">
      <div className="flex items-center gap-2 mb-3">
        <Megaphone className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl text-white">Promotions</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <input data-testid="wa-promo-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Promotion name" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
        <input data-testid="wa-promo-desc" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Short description (optional)" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        <input data-testid="wa-promo-slug" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="Public slug (e.g. spring-bonus → /promo/spring-bonus)" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm font-mono" />
        <input data-testid="wa-promo-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Landing headline (optional)" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
        <input data-testid="wa-promo-subhead" value={subhead} onChange={(e) => setSubhead(e.target.value)} placeholder="Landing subhead (optional)" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
        <input data-testid="wa-promo-cash" value={cash} onChange={(e) => setCash(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Cash $" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
        <input data-testid="wa-promo-points" value={points} onChange={(e) => setPoints(e.target.value.replace(/[^0-9]/g, ""))} placeholder="Points" className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 outline-none text-sm" />
        <select data-testid="wa-promo-criteria" value={criteria} onChange={(e) => setCriteria(e.target.value)} className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none text-sm">
          <option value="manual" className="bg-[#0c0915]">Manual award</option>
          <option value="signup_referral" className="bg-[#0c0915]">When a referral is approved</option>
          <option value="first_purchase" className="bg-[#0c0915]">First purchase</option>
          <option value="daily_login" className="bg-[#0c0915]">Daily login</option>
        </select>
        <button data-testid="wa-promo-create" onClick={create} disabled={busy} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2 rounded-lg inline-flex items-center justify-center gap-1 disabled:opacity-50">
          <Plus className="w-4 h-4" /> Create
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((p) => (
          <div key={p.id} data-testid={`wa-promo-${p.id}`} className="rounded-xl bg-black/30 border border-white/10 p-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="font-bold text-white truncate">{p.name}</div>
              <div className="text-xs text-white/60 truncate">{p.description || p.criteria_type}</div>
              <div className="text-[11px] mt-1 flex items-center gap-2 flex-wrap">
                {p.reward_cents > 0 && <span className="bg-emerald-500/20 text-emerald-300 font-bold px-2 py-0.5 rounded-full">+${(p.reward_cents / 100).toFixed(2)}</span>}
                {p.reward_points > 0 && <span className="bg-pink-500/20 text-pink-300 font-bold px-2 py-0.5 rounded-full">+{p.reward_points} pts</span>}
                <span className="bg-white/10 text-white/60 px-2 py-0.5 rounded-full">{p.criteria_type}</span>
                <span className="text-white/40">{p.current_claims} claimed</span>
                {p.slug && (
                  <a
                    href={`/promo/${p.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`wa-promo-slug-${p.id}`}
                    className="bg-tv-accent/20 text-tv-accent font-bold px-2 py-0.5 rounded-full hover:bg-tv-accent/30 inline-flex items-center gap-1"
                  >/promo/{p.slug} ↗</a>
                )}
              </div>
            </div>
            <button onClick={() => toggle(p.id)} className={`p-2 rounded-full ${p.is_active ? "bg-emerald-500/20 text-emerald-300" : "bg-white/10 text-white/40"}`} title={p.is_active ? "Pause" : "Resume"}><Power className="w-4 h-4" /></button>
            {p.slug && (
              <BlastButton promo={p} />
            )}
            {p.criteria_type === "manual" && (
              <button data-testid={`wa-promo-award-${p.id}`} onClick={() => setAwardFor(p)} className="bg-tv-accent hover:bg-yellow-400 text-black font-bold px-3 py-1.5 rounded-full text-xs">Award user</button>
            )}
          </div>
        ))}
        {rows.length === 0 && <div className="text-center text-white/40 py-6">No promotions yet</div>}
      </div>
      {awardFor && <AwardPromoModal promo={awardFor} onClose={() => setAwardFor(null)} onDone={() => { setAwardFor(null); load(); }} />}
    </section>
  );
}

function AwardPromoModal({ promo, onClose, onDone }) {  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!userId.trim()) { toast.error("user_id required"); return; }
    setBusy(true);
    try {
      await api.post("/admin/promotions/award", { promo_id: promo.id, user_id: userId.trim() });
      toast.success("Awarded");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="tv-glass rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="wa-award-modal">
        <h3 className="font-display font-black text-xl text-white mb-1">Award {promo.name}</h3>
        <div className="text-white/60 text-sm mb-3">+${(promo.reward_cents / 100).toFixed(2)} · +{promo.reward_points} pts</div>
        <input data-testid="wa-award-user-id" value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="user_id" className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-accent" />
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-2 rounded-lg">Cancel</button>
          <button data-testid="wa-award-submit" onClick={submit} disabled={busy} className="flex-1 bg-tv-accent hover:bg-yellow-400 text-black font-bold py-2 rounded-lg disabled:opacity-50">{busy ? "Awarding…" : "Award"}</button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────── Email blast button + modal ─────────────────── */
function BlastButton({ promo }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        data-testid={`wa-promo-blast-${promo.id}`}
        onClick={() => setOpen(true)}
        className="bg-fuchsia-500/90 hover:bg-fuchsia-400 text-white font-bold px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1"
        title="Send email blast"
      ><Send className="w-3 h-3" /> Blast</button>
      {open && <BlastModal promo={promo} onClose={() => setOpen(false)} />}
    </>
  );
}

function BlastModal({ promo, onClose }) {
  const reward = [];
  if (promo.reward_cents) reward.push(`$${(promo.reward_cents / 100).toFixed(2)}`);
  if (promo.reward_points) reward.push(`${promo.reward_points} pts`);

  const [audience, setAudience] = useState("all");
  const [subject, setSubject] = useState(`You've got ${reward.join(" + ") || "a bonus"} waiting on Tap IN TV`);
  const [bodyText, setBodyText] = useState(
    `${promo.landing_headline || promo.name}\n\n${promo.landing_subhead || promo.description || ""}\n\nYour reward: ${reward.join(" + ") || "an exclusive perk"}`
  );
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    api.get(`/admin/promotions/${promo.id}/blasts`).then((r) => setHistory(r.data || [])).catch(() => {});
  }, [promo.id]);

  const send = async (testMode) => {
    setBusy(true);
    try {
      const payload = {
        audience,
        origin: window.location.origin,
        subject: subject.trim(),
        body: bodyText,
      };
      if (testMode && testTo.trim()) payload.test_to = testTo.trim();
      const r = await api.post(`/admin/promotions/${promo.id}/blast`, payload);
      if (r.data.test) {
        toast.success(`Test sent to ${testTo}`);
      } else {
        toast.success(`Blast queued · ${r.data.sent} sent · ${r.data.skipped} skipped`);
      }
      const h = await api.get(`/admin/promotions/${promo.id}/blasts`);
      setHistory(h.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="tv-glass rounded-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-blast-modal">
        <div className="flex items-center gap-2 mb-1">
          <Send className="w-5 h-5 text-fuchsia-400" />
          <h3 className="font-display font-black text-xl text-white">Email blast · {promo.name}</h3>
        </div>
        <div className="text-white/60 text-sm mb-3">Sends a marketing email with a button linking to <span className="font-mono text-tv-accent">/promo/{promo.slug}</span>.</div>

        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Audience</label>
        <div className="flex gap-2 mb-3 flex-wrap">
          {[["all", "All users"], ["creators", "Creators"], ["viewers", "Viewers"]].map(([v, lbl]) => (
            <button
              key={v}
              data-testid={`wa-blast-audience-${v}`}
              onClick={() => setAudience(v)}
              className={`px-3 py-1.5 rounded-full text-xs font-bold ${audience === v ? "bg-fuchsia-500 text-white" : "bg-white/10 text-white/60"}`}
            >{lbl}</button>
          ))}
        </div>

        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Subject</label>
        <input data-testid="wa-blast-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-fuchsia-400 text-sm mb-3" />

        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Body</label>
        <textarea data-testid="wa-blast-body" value={bodyText} onChange={(e) => setBodyText(e.target.value)} rows={5} className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-fuchsia-400 text-sm mb-3" />

        <label className="block text-white/60 text-xs uppercase tracking-wider font-semibold mb-1">Test email (sends to one address only)</label>
        <div className="flex gap-2 mb-4">
          <input data-testid="wa-blast-test-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className="flex-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white outline-none focus:border-fuchsia-400 text-sm" />
          <button data-testid="wa-blast-send-test" onClick={() => send(true)} disabled={busy || !testTo.trim()} className="bg-white/10 hover:bg-white/20 text-white font-bold px-3 py-2 rounded-lg disabled:opacity-50">Send test</button>
        </div>

        {history.length > 0 && (
          <div className="bg-black/30 rounded-lg p-3 mb-4 max-h-32 overflow-y-auto">
            <div className="text-white/50 text-[10px] uppercase tracking-wider font-bold mb-1">Past blasts</div>
            {history.map((h) => {
              const ctr = h.sent_count > 0 ? Math.round(((h.clicks_count || 0) / h.sent_count) * 100) : 0;
              return (
                <div key={h.id} className="text-xs text-white/70 flex items-center gap-2 py-1 flex-wrap">
                  <span className="text-white/40 tabular-nums">{new Date(h.fired_at).toLocaleString()}</span>
                  <span className="bg-white/5 px-2 py-0.5 rounded">{h.audience}</span>
                  <span className="text-emerald-300">{h.sent_count} sent</span>
                  {h.skipped_count > 0 && <span className="text-rose-300">{h.skipped_count} skipped</span>}
                  <span className="text-cyan-300">{h.clicks_count || 0} clicks{ctr ? ` · ${ctr}% CTR` : ""}</span>
                  <span className="text-fuchsia-300">{h.signups_count || 0} signups</span>
                  {h.status === "queued" && <span className="text-amber-300 italic">queued…</span>}
                </div>
              );
            })}
          </div>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 bg-white/10 hover:bg-white/20 text-white font-bold py-2.5 rounded-lg">Close</button>
          <button
            data-testid="wa-blast-send-bulk"
            onClick={() => {
              if (!window.confirm(`Send this email to ${audience === "all" ? "ALL users" : audience}? This cannot be undone.`)) return;
              send(false);
            }}
            disabled={busy}
            className="flex-1 bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black py-2.5 rounded-lg inline-flex items-center justify-center gap-2 disabled:opacity-50"
          ><Send className="w-4 h-4" /> {busy ? "Sending…" : `Send to ${audience}`}</button>
        </div>
      </div>
    </div>
  );
}


/* ─────────────────────────── Conversion Leaderboard ──────────────────── */
function BlastsLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/blasts/leaderboard")
      .then((r) => setRows(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading && rows.length === 0) {
    return (
      <section className="rounded-2xl tv-glass p-5" data-testid="wa-leaderboard">
        <div className="text-white/50 text-sm">Loading leaderboard…</div>
      </section>
    );
  }
  if (rows.length === 0) {
    return null; // hide until at least one non-test blast exists
  }

  const top = rows[0];

  return (
    <section className="rounded-2xl tv-glass p-5" data-testid="wa-leaderboard">
      <div className="flex items-center gap-2 mb-3">
        <BarChart3 className="w-5 h-5 text-tv-accent" />
        <h3 className="font-display font-black text-xl text-white">Conversion leaderboard</h3>
        <span className="ml-auto text-white/40 text-xs">Sorted by signups → clicks → sends</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-white/40 text-[11px] uppercase tracking-wider border-b border-white/10">
            <tr>
              <th className="py-2 px-2">#</th>
              <th className="py-2 px-2">Promo</th>
              <th className="py-2 px-2 text-right">Sent</th>
              <th className="py-2 px-2 text-right">Opens</th>
              <th className="py-2 px-2 text-right">Clicks</th>
              <th className="py-2 px-2 text-right">Signups</th>
              <th className="py-2 px-2 text-right">OR</th>
              <th className="py-2 px-2 text-right">CTR</th>
              <th className="py-2 px-2 text-right">CR</th>
              <th className="py-2 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.promo_id} data-testid={`wa-lb-row-${r.promo_id}`} className={`border-b border-white/5 hover:bg-white/5 ${r.promo_id === top.promo_id ? "bg-tv-accent/5" : ""}`}>
                <td className="py-2 px-2 text-white/40 tabular-nums">
                  {i === 0 ? <Trophy className="w-4 h-4 text-tv-accent inline" /> : i + 1}
                </td>
                <td className="py-2 px-2">
                  <div className="font-bold text-white truncate max-w-[200px]">{r.promo_name}</div>
                  {r.promo_slug && (
                    <a href={`/promo/${r.promo_slug}`} target="_blank" rel="noreferrer" className="text-[10px] text-tv-accent/80 hover:text-tv-accent font-mono">/promo/{r.promo_slug}</a>
                  )}
                </td>
                <td className="py-2 px-2 text-right text-white tabular-nums">{r.sent.toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-cyan-300 tabular-nums">{r.opens.toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-emerald-300 tabular-nums">{r.clicks.toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-fuchsia-300 tabular-nums font-bold">{r.signups.toLocaleString()}</td>
                <td className="py-2 px-2 text-right text-cyan-300 tabular-nums">{r.open_rate}%</td>
                <td className="py-2 px-2 text-right text-emerald-300 tabular-nums">{r.ctr}%</td>
                <td className="py-2 px-2 text-right text-fuchsia-300 tabular-nums font-bold">{r.conversion_rate}%</td>
                <td className="py-2 px-2 text-[10px] text-white/40 whitespace-nowrap">
                  {r.last_fired_at ? new Date(r.last_fired_at).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-white/40 mt-3 flex flex-wrap gap-x-4 gap-y-1">
        <span><b className="text-cyan-300">OR</b> Open Rate (opens / sent)</span>
        <span><b className="text-emerald-300">CTR</b> Click-Through Rate (clicks / sent)</span>
        <span><b className="text-fuchsia-300">CR</b> Signup Conversion (signups / clicks)</span>
      </div>
    </section>
  );
}


/* ───────────────────── Live ad-revenue section ────────────────────── */
function LiveAdRevenueSection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [cpm, setCpm] = useState(800);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api
      .get(`/admin/ads/live-revenue?days=${days}&cpm_cents=${cpm}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [days, cpm]);
  useEffect(() => { load(); }, [load]);

  const dollars = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

  return (
    <section data-testid="live-ad-revenue-section" className="bg-black/40 border border-white/10 rounded-2xl p-6">
      <header className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold flex items-center gap-2"><BarChart3 className="w-3.5 h-3.5" /> Live broadcast revenue</div>
          <h3 className="font-display font-bold text-xl text-white">Ad-break payouts feed</h3>
          <p className="text-white/55 text-sm mt-0.5">Tally of ad-breaks fired during live shows. Multiply by your CPM rate to feed Stripe payouts.</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <label className="text-white/60 inline-flex items-center gap-1">Days
            <select
              data-testid="ad-rev-days"
              value={days}
              onChange={(e) => setDays(parseInt(e.target.value, 10))}
              className="bg-white/10 border border-white/15 rounded-md px-2 py-1 text-white"
            >
              {[7, 30, 90, 180, 365].map((d) => <option key={d} value={d} className="bg-[#0c0915]">{d}</option>)}
            </select>
          </label>
          <label className="text-white/60 inline-flex items-center gap-1">CPM ¢
            <input
              data-testid="ad-rev-cpm"
              type="number"
              min="0"
              value={cpm}
              onChange={(e) => setCpm(parseInt(e.target.value, 10) || 0)}
              className="w-20 bg-white/10 border border-white/15 rounded-md px-2 py-1 text-white"
            />
          </label>
        </div>
      </header>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <Stat label="Total ad-breaks"    value={(data?.total_ads || 0).toLocaleString()} testid="ad-rev-total-ads" />
        <Stat label="Est. impressions"   value={(data?.total_impressions || 0).toLocaleString()} testid="ad-rev-total-impressions" />
        <Stat label="Est. revenue"       value={dollars(data?.total_revenue_cents)} testid="ad-rev-total-revenue" highlight />
        <Stat label="Active streams"     value={(data?.top_streams?.length || 0).toString()} testid="ad-rev-stream-count" />
      </div>

      {/* Top streams table */}
      <div className="bg-black/30 border border-white/10 rounded-xl overflow-hidden">
        <table className="w-full text-sm" data-testid="ad-rev-top-streams">
          <thead className="bg-white/5 text-[10px] uppercase tracking-wider text-white/50">
            <tr>
              <th className="text-left px-3 py-2">Stream</th>
              <th className="text-left px-3 py-2 hidden md:table-cell">Creator</th>
              <th className="text-right px-3 py-2">Ads</th>
              <th className="text-right px-3 py-2 hidden md:table-cell">Impressions</th>
              <th className="text-right px-3 py-2">Revenue</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="text-center text-white/50 py-8">Loading…</td></tr>}
            {!loading && (data?.top_streams || []).length === 0 && (
              <tr><td colSpan={5} className="text-center text-white/40 py-10" data-testid="ad-rev-empty">No ad-breaks fired in this window yet.</td></tr>
            )}
            {!loading && (data?.top_streams || []).map((row, i) => (
              <tr key={row.stream_id || i} data-testid={`ad-rev-row-${row.stream_id}`} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-2 text-white truncate max-w-[260px]">{row.title || row.stream_id}</td>
                <td className="px-3 py-2 text-white/70 hidden md:table-cell truncate max-w-[180px]">{row.creator_name || "—"}</td>
                <td className="px-3 py-2 text-right text-white tabular-nums">{(row.ads || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-white/80 tabular-nums hidden md:table-cell">{(row.impressions || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-right text-emerald-300 font-bold tabular-nums">{dollars(row.revenue_cents)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[10px] text-white/40 mt-3">
        Revenue = (impressions ÷ 1000) × CPM. Default CPM ($8.00 / 1000) is an industry-average estimate — adjust to match your ad partner's actual rate. Each ad-break is weighted by the stream's then-current viewer-count when available, otherwise a 30-viewer floor.
      </div>
    </section>
  );
}

function Stat({ label, value, testid, highlight }) {
  return (
    <div data-testid={testid} className={`rounded-xl border ${highlight ? "border-emerald-500/40 bg-emerald-500/5" : "border-white/10 bg-black/30"} p-3`}>
      <div className="text-[10px] uppercase tracking-wider text-white/50 font-black">{label}</div>
      <div className={`mt-1 font-display font-black ${highlight ? "text-emerald-300" : "text-white"} text-2xl tabular-nums`}>{value}</div>
    </div>
  );
}
